import { useState, useEffect } from 'react';
import {
  resolveSpotImage,
  getCategoryPlaceholder,
  getCuratedAuthenticPhoto,
  SpotImageTarget,
} from '../services/imageResolver';

export interface UseSpotImageResult {
  imageUrl: string;
  isLoading: boolean;
  isError: boolean;
}

/**
 * React Hook for asynchronously resolving authentic spot photos with a 3-tier fallback chain.
 * Immediately serves cached images or clean category placeholders while resolving in background.
 */
export function useSpotImage(spot: SpotImageTarget | null | undefined): UseSpotImageResult {
  // Compute immediate synchronous placeholder or cached value
  const getInitialState = (): { url: string; loading: boolean } => {
    if (!spot) {
      return { url: getCategoryPlaceholder('default'), loading: false };
    }

    // 1. Primary: Dedicated local authentic photo or explicit verified imageUrl
    if (spot.imageUrl && spot.imageUrl.startsWith('/spots/')) {
      return { url: spot.imageUrl, loading: false };
    }
    if (spot.imageUrl && spot.imageUrl.startsWith('http') && !spot.imageUrl.includes('unsplash.com/photo-1538485399081')) {
      return { url: spot.imageUrl, loading: false };
    }
    // 2. Check curated authentic high-res dictionary (0ms Instant)
    const curated = getCuratedAuthenticPhoto(spot.nameKr, spot.name);
    if (curated) {
      return { url: curated, loading: false };
    }



    // 3. Check localStorage cache (ignore stale generic unsplash cache)
    const cacheKey = `spot-image:${spot.id || `${spot.lat.toFixed(4)},${spot.lng.toFixed(4)}`}`;
    try {
      if (typeof localStorage !== 'undefined') {
        const cached = localStorage.getItem(cacheKey);
        if (cached && !cached.includes('unsplash.com/photo-1538485399081')) {
          return { url: cached, loading: false };
        }
      }
    } catch {
      // LocalStorage access fallback
    }

    // 4. Check specific spot imageUrl
    if (spot.imageUrl && !spot.imageUrl.includes('unsplash.com/photo-1538485399081')) {
      return { url: spot.imageUrl, loading: false };
    }

    // 5. Default to category placeholder while resolving dynamically
    return { url: getCategoryPlaceholder(spot.category), loading: true };
  };

  const [imageUrl, setImageUrl] = useState<string>(() => getInitialState().url);
  const [isLoading, setIsLoading] = useState<boolean>(() => getInitialState().loading);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (!spot) {
      setImageUrl(getCategoryPlaceholder('default'));
      setIsLoading(false);
      return;
    }

    let isMounted = true;
    const cacheKey = `spot-image:${spot.id || `${spot.lat.toFixed(4)},${spot.lng.toFixed(4)}`}`;

    // Fast path 1: local authentic spot image or explicit verified imageUrl
    if (spot.imageUrl && spot.imageUrl.startsWith('/spots/')) {
      setImageUrl(spot.imageUrl);
      setIsLoading(false);
      return;
    }
    if (spot.imageUrl && spot.imageUrl.startsWith('http') && !spot.imageUrl.includes('unsplash.com/photo-1538485399081')) {
      setImageUrl(spot.imageUrl);
      setIsLoading(false);
      return;
    }

    // Fast path 1.5: Curated high-res authentic travel photos
    const curated = getCuratedAuthenticPhoto(spot.nameKr, spot.name);
    if (curated) {
      setImageUrl(curated);
      setIsLoading(false);
      return;
    }



    // Fast path 2: check localStorage cache (ignore stale generic unsplash cache)
    try {
      if (typeof localStorage !== 'undefined') {
        const cached = localStorage.getItem(cacheKey);
        if (cached && !cached.includes('unsplash.com/photo-1538485399081')) {
          setImageUrl(cached);
          setIsLoading(false);
          return;
        }
      }
    } catch {}

    setIsLoading(true);
    setIsError(false);

    resolveSpotImage(spot)
      .then((resolved) => {
        if (isMounted) {
          setImageUrl(resolved);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setImageUrl(getCategoryPlaceholder(spot.category));
          setIsLoading(false);
          setIsError(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [spot?.id, spot?.name, spot?.nameKr, spot?.lat, spot?.lng, spot?.category]);

  return { imageUrl, isLoading, isError };
}
